import React, { useMemo, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { intl, LanguageBaseContext, fetchData, HTTP_STATUS, Notifications } from "dmsbasereact";
import Swal from "sweetalert2";
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Button,
  Chip
} from "@mui/material";
import {
  School as TrainingIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  MedicalServices as DoctorIcon,
  LocalPharmacy as PharmacistIcon,
  Business as InstitutionIcon,
  Verified as VerifiedIcon
} from "@mui/icons-material";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { LenaLogo } from "../../components/LenaLogo";
import { DocumentsTab } from "../ProfileSettingsPage/tabs/DocumentsTab";
import { useInstitutionStatus } from "../../hooks/useInstitutionStatus";

const ACCOUNT_STATUS = {
  PENDING_CFM_VALIDATION: 1,
  PENDING_DOCUMENT_UPLOAD: 2,
  PENDING_APPROVAL: 3,
  PENDING_TRAINING: 4,
  ACTIVE: 5,
  REJECTED: 6,
  SUSPENDED: 7,
  PENDING_APPROVAL_GESTAO_LENA: 8,
  PENDING_APPROVAL_REGULATORIO: 9,
  PENDING_RECYCLING_TRAINING: 10,
};

const TYPE_ICONS = {
  doctor: <DoctorIcon sx={{ fontSize: 16 }} />,
  pharmacist: <PharmacistIcon sx={{ fontSize: 16 }} />,
  institution_responsible: <InstitutionIcon sx={{ fontSize: 16 }} />,
};

const getTypeLabel = (type) => {
  const labels = {
    doctor: intl.get("UserType.Doctor").d("Doctor"),
    pharmacist: intl.get("UserType.Pharmacist").d("Pharmacist"),
    institution_responsible: intl.get("UserType.InstitutionResponsible").d("Institution Responsible"),
  };
  return labels[type] || type;
};

/**
 * Build dynamic steps based on user types and their statuses.
 * @param {Array} userTypes - User types array with status info
 * @param {string} _language - Current language (triggers recalculation on change)
 * @param {boolean} needsTraining - Whether training is required but not completed (person-level)
 * @param {Array} institutionStatuses - Per-institution approval statuses
 */
const buildDynamicSteps = (userTypes, _language, needsTraining = false, institutionStatuses = []) => {
  const steps = [];

  // Check what types the user has and their statuses
  const doctorType = userTypes?.find(t => t.type === "doctor");
  const pharmacistType = userTypes?.find(t => t.type === "pharmacist");
  const instRespType = userTypes?.find(t => t.type === "institution_responsible");
  const distributorRespType = userTypes?.find(t => t.type === "distributor_responsible");

  // Step 1: CFM Validation (only if user is doctor)
  if (doctorType) {
    steps.push({
      key: "cfm",
      label: intl.get("Onboarding.Step.CfmValidation").d("CRM Validation"),
      type: "doctor",
      requiredStatus: ACCOUNT_STATUS.PENDING_CFM_VALIDATION,
      isComplete: doctorType.status !== ACCOUNT_STATUS.PENDING_CFM_VALIDATION,
    });
  }

  // Step 2: Documents for non-doctor types (pharmacist, institution_responsible, distributor_responsible)
  // Documents step is needed if user has pharmacist, institution_responsible or distributor_responsible types
  // The step is complete when ALL institutions either have documents submitted OR user is ACTIVE
  const hasNonDoctorTypes = pharmacistType || instRespType || distributorRespType;

  if (hasNonDoctorTypes) {
    // Check per-institution status: step is complete only when ALL institutions are handled
    // An institution is "handled" if:
    // - User is ACTIVE at that institution (auto-approved, no docs needed)
    // - OR user has documents_submitted=true for that institution
    const institutionsNeedingDocs = institutionStatuses.filter(inst => {
      const isActive = inst.status === ACCOUNT_STATUS.ACTIVE;
      const hasSubmittedDocs = inst.documents_submitted;
      // Institution needs docs if: NOT active AND NOT submitted docs yet
      return !isActive && !hasSubmittedDocs;
    });

    // Documents step is complete when no institutions need documents
    const documentStepComplete = institutionStatuses.length > 0 && institutionsNeedingDocs.length === 0;

    // Build list of types that need docs (for display purposes)
    const typesForDocs = [];
    if (pharmacistType) typesForDocs.push("pharmacist");
    if (instRespType) typesForDocs.push("institution_responsible");
    if (distributorRespType) typesForDocs.push("distributor_responsible");

    steps.push({
      key: "documents",
      label: intl.get("Onboarding.Step.Documents").d("Submit Documents"),
      types: typesForDocs,
      isComplete: documentStepComplete,
      pendingTypes: documentStepComplete ? [] : typesForDocs,
    });
  }

  // Step 3: Approval
  steps.push({
    key: "approval",
    label: intl.get("Onboarding.Step.Approval").d("Approval"),
    isComplete: userTypes?.every(t =>
      t.status === ACCOUNT_STATUS.PENDING_TRAINING ||
      t.status === ACCOUNT_STATUS.ACTIVE
    ),
  });

  // Step 4: Training
  // Training is complete only if ALL types are ACTIVE AND training is done (person-level)
  steps.push({
    key: "training",
    label: intl.get("Onboarding.Step.Training").d("Training"),
    isComplete: userTypes?.every(t => t.status === ACCOUNT_STATUS.ACTIVE) && !needsTraining,
  });

  // Step 5: Complete
  // Complete when ALL types are ACTIVE AND training is done
  steps.push({
    key: "complete",
    label: intl.get("Onboarding.Step.Complete").d("Complete"),
    isComplete: userTypes?.every(t => t.status === ACCOUNT_STATUS.ACTIVE) && !needsTraining,
  });

  return steps;
};

/**
 * Determine the current active step index based on user types and statuses.
 */
const getActiveStepIndex = (steps, userTypes) => {
  // Find the first step that is not complete
  for (let i = 0; i < steps.length; i++) {
    if (!steps[i].isComplete) {
      return i;
    }
  }
  return steps.length - 1; // All complete
};

/**
 * Get the current step's content type based on what action is needed.
 * @param {Array} userTypes - User types array with status info
 * @param {number} accountStatus - Current account status
 * @param {boolean} needsTraining - Whether training is required but not completed (person-level)
 * @param {Array} institutionStatuses - Per-institution approval statuses
 */
const getCurrentStepAction = (userTypes, accountStatus, needsTraining = false, institutionStatuses = []) => {
  const doctorType = userTypes?.find(t => t.type === "doctor");
  const pharmacistType = userTypes?.find(t => t.type === "pharmacist");
  const instRespType = userTypes?.find(t => t.type === "institution_responsible");
  const distributorRespType = userTypes?.find(t => t.type === "distributor_responsible");
  const hasNonDoctorTypes = pharmacistType || instRespType || distributorRespType;

  // Doctor needs CFM validation if in PENDING_CFM_VALIDATION status
  // OR if doctor-only user is in PENDING_DOCUMENT_UPLOAD (inconsistent state from old data)
  if (doctorType?.status === ACCOUNT_STATUS.PENDING_CFM_VALIDATION ||
      (doctorType && !hasNonDoctorTypes && doctorType.status === ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD)) {
    return { action: "cfm_validation", types: ["doctor"] };
  }

  if (accountStatus === ACCOUNT_STATUS.REJECTED) {
    // For rejected accounts: only show document upload if there are non-doctor types
    if (hasNonDoctorTypes) {
      const allTypes = userTypes?.map(t => t.type) || [];
      return { action: "document_upload", types: allTypes };
    }
    // Doctor-only rejected should go back to CFM validation
    return { action: "cfm_validation", types: ["doctor"] };
  }

  // Check per-institution status: if any institution needs documents, show document upload
  if (hasNonDoctorTypes && institutionStatuses.length > 0) {
    const institutionsNeedingDocs = institutionStatuses.filter(inst => {
      const isActive = inst.status === ACCOUNT_STATUS.ACTIVE;
      const hasSubmittedDocs = inst.documents_submitted;
      return !isActive && !hasSubmittedDocs;
    });

    if (institutionsNeedingDocs.length > 0) {
      const typesForDocs = [];
      if (pharmacistType) typesForDocs.push("pharmacist");
      if (instRespType) typesForDocs.push("institution_responsible");
      if (distributorRespType) typesForDocs.push("distributor_responsible");
      return { action: "document_upload", types: typesForDocs };
    }
  }

  // Fallback: Only pharmacists, institution_responsibles and distributor_responsibles need document upload
  // Doctors NEVER need document upload - they go through CFM validation instead
  const typesNeedingDocs = userTypes?.filter(t =>
    t.status === ACCOUNT_STATUS.PENDING_DOCUMENT_UPLOAD &&
    (t.type === "pharmacist" || t.type === "institution_responsible" || t.type === "distributor_responsible")
  ) || [];
  if (typesNeedingDocs.length > 0) {
    return { action: "document_upload", types: typesNeedingDocs.map(t => t.type) };
  }

  // PRIORITY 1: Check if user is approved AND needs training
  // Training check happens BEFORE approval check because training is mandatory and blocks user
  if (institutionStatuses.length > 0) {
    // Check if approved at ANY institution
    const hasApprovedInstitution = institutionStatuses.some(inst =>
      inst.status === ACCOUNT_STATUS.ACTIVE
    );

    // If approved, check training completion (person-level)
    if (hasApprovedInstitution && needsTraining) {
      return { action: "training", types: userTypes };
    }
  }

  // PRIORITY 2: Check if waiting for approval (per-institution check takes priority)
  // First check institutionStatuses for more accurate per-institution status
  if (institutionStatuses.length > 0) {
    const institutionsInApproval = institutionStatuses.filter(inst =>
      inst.status === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA ||
      inst.status === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO
    );
    if (institutionsInApproval.length > 0) {
      return { action: "waiting_approval", types: userTypes };
    }
  }

  // Fallback: check userTypes for approval status (legacy/backward compatibility)
  const typesInApproval = userTypes?.filter(t =>
    t.status === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA ||
    t.status === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO
  ) || [];
  if (typesInApproval.length > 0) {
    return { action: "waiting_approval", types: typesInApproval };
  }

  // Check if ACTIVE but training not completed (person-level training check)
  // This handles the case where institution approval is ACTIVE but training is pending
  if (needsTraining) {
    return { action: "training", types: [] };
  }

  // All active and training complete
  return { action: "complete", types: [] };
};

export const OnboardingPage = ({ accountStatus, userType, userTypes = [], statusReason, onStatusChange, onPoll, needsTraining = false }) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageBaseContext);

  // Get user's institution approval statuses
  const { institutionStatuses } = useInstitutionStatus();

  // CFM validation states
  const [cfmValidating, setCfmValidating] = useState(false);
  const [cfmValidated, setCfmValidated] = useState(false);
  const [cfmError, setCfmError] = useState(null);

  // Use single-type fallback if userTypes is empty - memoized to avoid recreating on every render
  const effectiveUserTypes = useMemo(() => {
    return userTypes.length > 0 ? userTypes : [
      { type: userType, status: accountStatus, is_active: accountStatus === ACCOUNT_STATUS.ACTIVE }
    ];
  }, [userTypes, userType, accountStatus]);

  const steps = useMemo(() => buildDynamicSteps(effectiveUserTypes, language, needsTraining, institutionStatuses), [effectiveUserTypes, language, needsTraining, institutionStatuses]);
  const activeStepIndex = useMemo(() => getActiveStepIndex(steps, effectiveUserTypes), [steps, effectiveUserTypes]);
  const currentAction = useMemo(() => getCurrentStepAction(effectiveUserTypes, accountStatus, needsTraining, institutionStatuses), [effectiveUserTypes, accountStatus, needsTraining, institutionStatuses]);

  // Poll for status changes when on waiting_approval step (silent, no loading spinner)
  useEffect(() => {
    if (currentAction?.action !== 'waiting_approval') {
      return;
    }

    const pollFn = onPoll || onStatusChange;
    const pollInterval = setInterval(() => {
      pollFn();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [currentAction?.action, onPoll, onStatusChange]);

  const isRejected = accountStatus === ACCOUNT_STATUS.REJECTED;
  const isSuspended = accountStatus === ACCOUNT_STATUS.SUSPENDED;
  const isMultiType = effectiveUserTypes.length > 1;

  const handleCfmValidation = useCallback(async () => {
    setCfmValidating(true);
    setCfmError(null);

    try {
      const response = await fetchData("/doctor/validate_cfm", "POST", true);
      const data = response?.data?.data || response?.data || {};

      if (response.status === HTTP_STATUS.OK_200 && data?.validated) {
        setCfmValidated(true);
        Notifications.toast(
          intl.get("CfmValidationSuccess").d("CRM validated successfully!"),
          Notifications.TYPE_SUCCESS
        );
        if (onStatusChange) {
          setTimeout(() => onStatusChange(), 1500);
        }
      } else {
        const errorMsg = data?.situation_text ||
          response?.data?.detail ||
          intl.get("CfmValidationFailed").d("Could not validate CRM");

        setCfmError(errorMsg);
        Swal.fire({
          icon: "error",
          title: intl.get("CfmValidationError").d("Validation Error"),
          text: errorMsg,
        });
      }
    } catch (error) {
      setCfmError(intl.get("CfmValidationNetworkError").d("Connection error. Please try again."));
    } finally {
      setCfmValidating(false);
    }
  }, [onStatusChange]);

  // Suspended users see a simple message
  if (isSuspended) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default", p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6">
              {intl.get("AccountStatus.Suspended.Title").d("Account Suspended")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {intl.get("AccountStatus.Suspended.Message").d("Your account is suspended. Please contact support.")}
            </Typography>
          </Alert>
        </Paper>
      </Box>
    );
  }

  const renderRejectionAlert = () => {
    if (!isRejected) return null;
    return (
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3, "& .MuiAlert-message": { width: "100%" } }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {intl.get("AccountStatus.Rejected.Title").d("Account Rejected")}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{intl.get("RejectionReason").d("Rejection Reason")}:</strong>{" "}
          {statusReason || intl.get("NoReasonProvided").d("No reason provided")}
        </Typography>
      </Alert>
    );
  };

  const getApprovalSubtitle = () => {
    // Check the status of types in approval
    // Note: Only Gestão Lena approval step is now used for user approval
    // PENDING_APPROVAL_REGULATORIO is kept for backward compatibility with existing data
    const typesInApproval = effectiveUserTypes.filter(t =>
      t.status === ACCOUNT_STATUS.PENDING_APPROVAL_GESTAO_LENA ||
      t.status === ACCOUNT_STATUS.PENDING_APPROVAL_REGULATORIO
    );

    if (typesInApproval.length > 0) {
      return intl.get("Onboarding.Approval.WaitingGestaoLena").d("Waiting for approval from the Lena Management team");
    }
    return null;
  };

  const renderStepContent = () => {
    switch (currentAction.action) {
      case "cfm_validation":
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            {renderRejectionAlert()}
            {cfmValidating ? (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {intl.get("Onboarding.CfmValidating").d("Validating CRM...")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.get("Onboarding.CfmValidating.Description").d(
                    "Querying the Federal Council of Medicine. Please wait..."
                  )}
                </Typography>
              </>
            ) : cfmValidated ? (
              <>
                <VerifiedIcon sx={{ fontSize: 60, color: "success.main", mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 1, color: "success.main" }}>
                  {intl.get("Onboarding.CfmValidated").d("CRM Validated!")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.get("Onboarding.CfmValidated.Description").d(
                    "Your registration was successfully validated. Please wait for the page to refresh..."
                  )}
                </Typography>
              </>
            ) : (
              <>
                <DoctorIcon sx={{ fontSize: 60, color: "primary.main", mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {intl.get("Onboarding.Step.CfmValidation").d("CRM Validation")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {intl.get("Onboarding.Step.CfmValidation.Description2").d(
                    "Click the button below to validate your registration with the Federal Council of Medicine (CFM)."
                  )}
                </Typography>

                {cfmError && (
                  <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                    {cfmError}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<VerifiedIcon />}
                  onClick={handleCfmValidation}
                  sx={{ mb: 3 }}
                >
                  {intl.get("Onboarding.Action.ValidateCfm").d("Validate CRM")}
                </Button>

                <Alert severity="info" sx={{ textAlign: "left" }}>
                  {intl.get("Onboarding.CfmValidationTip2").d(
                    "Validation is performed automatically by querying CFM data. Make sure your CRM is correct."
                  )}
                </Alert>
              </>
            )}
          </Box>
        );

      case "document_upload":
        // Show unified document upload area
        const typesForDocs = currentAction.types;
        return (
          <Box>
            {renderRejectionAlert()}
            <Typography variant="h6" sx={{ mb: 1 }}>
              {isRejected
                ? intl.get("Onboarding.Step.Documents.Resubmit").d("Correct and Resubmit Documents")
                : intl.get("Onboarding.Step.Documents").d("Submit Documents")
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {intl.get("Onboarding.Step.Documents.Description").d(
                "Submit the required documents for account validation."
              )}
            </Typography>

            {/* Requirements explanation based on user types */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{intl.get("Onboarding.Documents.RequirementsTitle").d("Documents required for approval:")}</strong>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {typesForDocs.includes("pharmacist") && (
                  <li>
                    <Typography variant="body2">
                      <strong>{intl.get("UserType.Pharmacist").d("Pharmacist")}:</strong>{" "}
                      {intl.get("Onboarding.Documents.PharmacistRequirements").d(
                        "CRF Certificate, Institutional Link Proof or Employment Contract"
                      )}
                    </Typography>
                  </li>
                )}
                {typesForDocs.includes("institution_responsible") && (
                  <li>
                    <Typography variant="body2">
                      <strong>{intl.get("UserType.InstitutionResponsible").d("Institution Responsible")}:</strong>{" "}
                      {intl.get("Onboarding.Documents.InstitutionResponsibleRequirements").d(
                        "Institutional Link Proof, Employment Contract or Professional License"
                      )}
                    </Typography>
                  </li>
                )}
                {typesForDocs.includes("distributor_responsible") && (
                  <li>
                    <Typography variant="body2">
                      <strong>{intl.get("UserType.DistributorResponsible").d("Distributor Responsible")}:</strong>{" "}
                      {intl.get("Onboarding.Documents.DistributorResponsibleRequirements").d(
                        "Employment Contract or Professional License"
                      )}
                    </Typography>
                  </li>
                )}
              </Box>
            </Alert>

            {/* Single unified DocumentsTab */}
            <DocumentsTab
              userType={typesForDocs.length === 1 ? typesForDocs[0] : null}
              onStatusChange={onStatusChange}
            />
          </Box>
        );

      case "waiting_approval":
        const hasDoctor = currentAction.types?.some(t => t.type === 'doctor');
        const approvalMessageKey = hasDoctor
          ? "Onboarding.Step.Approval.Description"
          : "Onboarding.Step.Approval.Description.Documents";
        const approvalMessageDefault = hasDoctor
          ? "Your registration is being reviewed by the Lena team."
          : "Your documents are being reviewed by the Lena team.";

        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {intl.get("Onboarding.Step.Approval").d("Approval")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {intl.get(approvalMessageKey).d(approvalMessageDefault)}
            </Typography>

            {/* Show per-institution approval progress */}
            {institutionStatuses.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                  {intl.get("Onboarding.InstitutionStatus").d("Status by Institution:")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                  {institutionStatuses.map(inst => (
                    <Chip
                      key={inst.institution_pk}
                      icon={inst.is_approved ? <CompleteIcon /> : <WarningIcon />}
                      label={`${intl.get("Onboarding.UserStatusAt").d("Status do usuário em")} ${inst.institution_name}: ${inst.status_label}`}
                      size="small"
                      color={inst.is_approved ? "success" : "default"}
                      variant={inst.is_approved ? "filled" : "outlined"}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Fallback: Show which types are in approval if no institution data */}
            {institutionStatuses.length === 0 && isMultiType && (
              <Box sx={{ mb: 2, display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                {currentAction.types.map(t => (
                  <Chip
                    key={t.type}
                    icon={TYPE_ICONS[t.type]}
                    label={`${getTypeLabel(t.type)}: ${t.status_label || intl.get("Waiting").d("Waiting")}`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            {getApprovalSubtitle() && (
              <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                {getApprovalSubtitle()}
              </Typography>
            )}
            <Alert severity="info" sx={{ mt: 3, textAlign: "left" }}>
              {intl.get("Onboarding.ApprovalTip").d("Tip: Check your email regularly.")}
            </Alert>

            {!hasDoctor && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
                {intl.get("Onboarding.UpdateDocumentsTip").d("Need to update or send more documents?")}{" "}
                <strong>
                  {intl.get("Onboarding.UpdateDocumentsLocation").d("Go to Profile Settings → Documents tab")}
                </strong>
              </Alert>
            )}
          </Box>
        );

      case "training":
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <TrainingIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {intl.get("Onboarding.Step.Training").d("Training")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {intl.get("Onboarding.Step.Training.Description").d(
                "Watch the mandatory training videos."
              )}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<TrainingIcon />}
              onClick={() => navigate(APP_ROUTES.TrainingVideo)}
            >
              {intl.get("Onboarding.Action.StartTraining").d("Start Training")}
            </Button>
          </Box>
        );

      case "complete":
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CompleteIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {intl.get("Onboarding.Step.Complete").d("Complete")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.get("Onboarding.Step.Complete.Description").d(
                "Congratulations! You have full access to the platform."
              )}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "background.default",
        p: { xs: 2, md: 4 }
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <LenaLogo variant="large" showLabel={false} />
        <Typography
          variant="h4"
          sx={{ mt: 2, fontWeight: "bold", color: isRejected ? "warning.main" : "primary.main" }}
        >
          {isRejected
            ? intl.get("Onboarding.Welcome.Rejected").d("Action Required")
            : intl.get("Onboarding.Welcome").d("Welcome to Lena!")
          }
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {isRejected
            ? intl.get("Onboarding.Subtitle.Rejected").d("Fix the identified issues")
            : intl.get("Onboarding.Subtitle").d("Complete the steps below to activate your account")
          }
        </Typography>

        {/* Show type chips for multi-type users */}
        {isMultiType && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
            {effectiveUserTypes.map(t => (
              <Chip
                key={t.type}
                icon={TYPE_ICONS[t.type]}
                label={getTypeLabel(t.type)}
                size="small"
                color={t.is_active ? "success" : "default"}
                variant={t.is_active ? "filled" : "outlined"}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Stepper */}
      <Box sx={{ width: "100%", maxWidth: 900, mb: 4 }}>
        <Stepper activeStep={activeStepIndex} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.key} completed={step.isComplete}>
              <StepLabel
                error={isRejected && index === activeStepIndex}
                StepIconProps={{
                  sx: {
                    fontSize: 28,
                    ...(index === activeStepIndex && !isRejected && { color: "primary.main" }),
                    ...(isRejected && index === activeStepIndex && { color: "warning.main" })
                  }
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          maxWidth: 800,
          width: "100%",
          ...(isRejected && { borderTop: 4, borderColor: "warning.main" })
        }}
      >
        {renderStepContent()}
      </Paper>

      {/* Help text */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
        {intl.get("Onboarding.Help").d("Precisa de ajuda? Entre em contato com o suporte.")}
      </Typography>
    </Box>
  );
};
